import { useEffect, useRef, useState, useContext, useMemo } from "react";
import { createOpenSeadragon } from "../viewer/Custom-OpenSeadragon";
import { useAnnotationInfo } from "../../hooks/useAnnotationInfo";
import styled from "styled-components";
import { AnnotationInfoPanel as AnnotationInfoPanelComponent } from "./AnnotationInfoPanel";
import { AnnotationPanel } from "./AnnotationPanel";
import { ViewerContext } from "../../page/Viewer";
import { patchUserCustomSetting } from "../../api/user";
import { ViewerToggleManager } from "../button";
import { ViewerId, annotationLabelScaleAtom } from "../../atom";
import { useRecoilValue } from "recoil";
import { registerViewer, unregisterViewer } from "./viewerRegistry";
interface OSDImageViewerProps {
  imagePath?: string; // 이미지 경로 (URL)
  photoId?: number; // 이미지 전용 어노테이션을 위한 photoId
  isAnno?: boolean;
  isUserSet?: boolean;
  isGrossFile?: boolean;

  onGetAnnotations?: (photoId: number) => Promise<any>;
  onSaveAnnotation?: (photoId: number, path: any, name: string) => Promise<void>;
  onAnnotationClick?: (event: any) => void;

  viewerId?: ViewerId;
  onActivate?: () => void;
  showAnnotationButton?: boolean;
  externalAnnotations?: any[] | null;
  onAnnotationsChange?: (annotations: any[]) => void;
}

const buildImageAnnotationPathList = (annotationList?: any[]) => {
  const cloneAnnotationInfo = (annoInfo: any) =>
    typeof structuredClone === "function"
      ? structuredClone(annoInfo)
      : JSON.parse(JSON.stringify(annoInfo));

  if (!annotationList?.length) {
    return [];
  }

  return annotationList.reduce<any[]>((acc, path) => {
    if (!path?.annoInfo) {
      return acc;
    }

    const annoInfo = cloneAnnotationInfo(path.annoInfo);
    if (annoInfo && Array.isArray(annoInfo) && annoInfo[1]) {
      const annotationId = path.antnGrossPhotoId || path.antnPhotoId || path.annoId;
      annoInfo[1].id = `annotation${annotationId}`;
      annoInfo[1].label = path.content;
      acc.push(annoInfo);
    }

    return acc;
  }, []);
};

const OSDImageViewer = ({
  imagePath,
  photoId,
  isAnno = true,
  isUserSet = false,
  isGrossFile = false,
  onGetAnnotations,
  onSaveAnnotation,
  viewerId,
  onActivate,
  showAnnotationButton = true,
  externalAnnotations,
  onAnnotationsChange,
}: OSDImageViewerProps) => {
  const osdContainerId = viewerId ? `openseadragon-img-${viewerId}` : "openseadragon";
  const [viewer, setViewer] = useState<any>(null);
  const viewerRef = useRef<any>(null);
  const [annotations, setAnnotations] = useState<any>(null);
  const [currentPhoto, setCurrentPhoto] = useState<any>(null);
  const [showAnnotationPanel, setShowAnnotationPanel] = useState(false);
  const { userCustomSet, setUserCustomSet } = useContext(ViewerContext);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const photoIdRef = useRef(photoId);
  const onGetAnnotationsRef = useRef(onGetAnnotations);
  const onSaveAnnotationRef = useRef(onSaveAnnotation);
  const onAnnotationsChangeRef = useRef(onAnnotationsChange);
  onAnnotationsChangeRef.current = onAnnotationsChange;
  const onActivateRef = useRef(onActivate);
  onActivateRef.current = onActivate;

  const updateAnnotations = (data: any) => {
    const list = Array.isArray(data) ? data : [];
    setAnnotations(list);
    onAnnotationsChangeRef.current?.(list);
  };

  // 어노테이션 정보 훅 사용 (이미지 / gross 모드)
  const annotationInfo = useAnnotationInfo({
    currentSlide: currentPhoto,
    setCurrentSlide: setCurrentPhoto,
    isImageMode: !isGrossFile,
    isGrossMode: isGrossFile,
    photoId: !isGrossFile ? photoId : undefined,
    grossPhotoId: isGrossFile ? photoId : undefined,
    viewerContainerId: osdContainerId,
  });
  const annotationInfoRef = useRef(annotationInfo);
  const annotationPathList = useMemo(
    () => buildImageAnnotationPathList(annotations || currentPhoto?.annotations),
    [annotations, currentPhoto?.annotations]
  );

  const handleAnnotationClickWrapper = (event: any) => {
    annotationInfoRef.current.handleAnnotationClick(event);
  };

  useEffect(() => {
    annotationInfoRef.current = annotationInfo;
  }, [annotationInfo]);

  useEffect(() => {
    photoIdRef.current = photoId;
    onGetAnnotationsRef.current = onGetAnnotations;
    onSaveAnnotationRef.current = onSaveAnnotation;
  }, [photoId, onGetAnnotations, onSaveAnnotation]);

  // currentPhoto가 변경되면 annotations도 업데이트 (정보창에서 수정·삭제한 경우)
  useEffect(() => {
    if (currentPhoto?.annotations) {
      updateAnnotations(currentPhoto.annotations);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPhoto]);

  // 외부(사이드 탭)에서 목록이 바뀌면 반영
  useEffect(() => {
    if (externalAnnotations !== undefined) {
      setAnnotations(externalAnnotations);
    }
  }, [externalAnnotations]);

  useEffect(() => {
    const newViewer = createOpenSeadragon({
      id: osdContainerId,
      showScalebar: false,
      useRotationSlider: userCustomSet?.useViewerRotationSlider ?? true,
      useFiltering: userCustomSet?.useGammaSlider ?? true,
      showNavigator: userCustomSet?.useViewerThumbnail ?? true,
      useCapture: userCustomSet?.useCaptureButton ?? true,
      saveAnnotation: true,
      isAnno:false,
      enableRulerMeasurement: false, // 이미지 뷰어는 MPP가 없어서 룰러 길이 측정 비활성화
      isPlainImage: true,

      // 어노테이션 조회
      getAnnotations: async () => {
        if (onGetAnnotationsRef.current && photoIdRef.current) {
          const data = await onGetAnnotationsRef.current(photoIdRef.current);
          updateAnnotations(data);
          return buildImageAnnotationPathList(data);
        }
        return null;
      },

      // 어노테이션 저장
      addAnnotation: async (path: any, name: any, rulerValue: any) => {
        if (onSaveAnnotationRef.current && photoIdRef.current) {
          const finalName = rulerValue ? `${name} / (${rulerValue}mm)` : name;
          await onSaveAnnotationRef.current(photoIdRef.current, path, finalName);
          if (onGetAnnotationsRef.current) {
            const data = await onGetAnnotationsRef.current(photoIdRef.current);
            updateAnnotations(data);
          }
        }
      },

      // 어노테이션 클릭 이벤트
      annotationClickEvent: handleAnnotationClickWrapper,
      useInvertedZoom: userCustomSet ? userCustomSet.useInvertedZoom : false,
    });

    viewerRef.current = newViewer;
    setViewer(newViewer);
    if (viewerId) registerViewer(viewerId, newViewer);

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      if (viewerId) unregisterViewer(viewerId, newViewer);
      viewerRef.current = null;
      newViewer.destroy();
    };
  }, []);

  useEffect(() => {
    if (!viewer) {
      return;
    }

    viewer.updateRuntimeOptions?.({
      useRotationSlider: userCustomSet?.useViewerRotationSlider ?? true,
      useFiltering: userCustomSet?.useGammaSlider ?? true,
      showNavigator: userCustomSet?.useViewerThumbnail ?? true,
      useCapture: userCustomSet?.useCaptureButton ?? true,
      useInvertedZoom: userCustomSet?.useInvertedZoom ?? false,
      gammaValue: userCustomSet?.userGammaValue ?? 1,
    });
    viewer.refreshUiControls?.();
  }, [
    viewer,
    userCustomSet?.useViewerRotationSlider,
    userCustomSet?.useGammaSlider,
    userCustomSet?.useViewerThumbnail,
    userCustomSet?.useCaptureButton,
    userCustomSet?.useInvertedZoom
  ]);

  // annotation 라벨 글씨 배율
  const labelScale = useRecoilValue(annotationLabelScaleAtom);
  useEffect(() => {
    viewer?.setAnnotationLabelScale?.(labelScale);
  }, [viewer, labelScale]);

  useEffect(() => {
    if (!viewer || !imagePath) {
      return;
    }

    setAnnotations(null);
    setCurrentPhoto(null);

    const sourceUrl = isGrossFile ? `${process.env.REACT_APP_BACKEND_URL}/api/gross-photo/file/${imagePath}` : `${process.env.REACT_APP_BACKEND_URL}/api/photo/${imagePath}`;
    if (viewer.currentSourceUrl !== sourceUrl) {
      viewer.currentSourceUrl = sourceUrl;
      viewer.open({
        type: "image",
        url: sourceUrl,
      });
    }
  }, [viewer, imagePath]);

  useEffect(() => {
    if (!viewer) {
      return;
    }

    if (isAnno && !viewer.areAnnotationsActive?.()) {
      viewer.initializeAnnotations?.();
    }

    if (!isAnno && viewer.areAnnotationsActive?.()) {
      viewer.cleanAnnotations?.();
    }
  }, [viewer, isAnno]);

  // 캔버스를 누르면 이 칸을 활성화 (듀얼 뷰어)
  useEffect(() => {
    if (!viewer) return;
    const handleCanvasPress = () => onActivateRef.current?.();
    viewer.addHandler("canvas-press", handleCanvasPress);
    viewer.addHandler("canvas-click", handleCanvasPress);
    return () => {
      viewer.removeHandler("canvas-press", handleCanvasPress);
      viewer.removeHandler("canvas-click", handleCanvasPress);
    };
  }, [viewer]);

  // 줌/팬 핸들러
  useEffect(() => {
    if (viewer) {
      const handleZoomPan = () => {
        if (!annotationInfoRef.current.showEditModal) {
          annotationInfoRef.current.closeAnnotationInfo();
        }
      };

      viewer.addHandler('zoom', handleZoomPan);
      viewer.addHandler('pan', handleZoomPan);

      return () => {
        viewer.removeHandler('zoom', handleZoomPan);
        viewer.removeHandler('pan', handleZoomPan);
      };
    }
  }, [viewer]);

  // userGammaValue가 변경될 때 OSD 필터 및 슬라이더 업데이트
  useEffect(() => {
    if (!viewer || !userCustomSet?.useGammaSlider) return;
    viewer.setGammaValue?.(userCustomSet?.userGammaValue ?? 1);
  }, [viewer, userCustomSet?.userGammaValue, userCustomSet?.useGammaSlider]);

  // 감마 슬라이더 이벤트 리스너 추가 (debounce 적용)
  useEffect(() => {
    if (!viewer) return;

    viewer.onGammaInput = (newValue: number) => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      debounceTimerRef.current = setTimeout(() => {
        setUserCustomSet((prev: any) => {
          const nextSettings = prev?.userGammaValue === newValue
            ? prev
            : { ...prev, userGammaValue: newValue };

          if (nextSettings !== prev) {
            patchUserCustomSetting(nextSettings).catch((error) => {
              console.error("Failed to persist gamma value:", error);
            });
          }

          return nextSettings;
        });
      }, 300);
    };

    return () => {
      viewer.onGammaInput = null;
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [viewer, setUserCustomSet]);


  // 어노테이션 데이터를 뷰어에 설정 (annotations state 기반)
  useEffect(() => {
    if (!viewer || !viewer.areAnnotationsActive?.()) {
      return;
    }

    if (!userCustomSet?.useAnnotation || !isAnno) {
      viewer.cleanAnnotations?.();
      return;
    }

    const timer = setTimeout(() => {
      viewer.setAnnotation?.(annotationPathList);
    }, 50);

    return () => clearTimeout(timer);
  }, [viewer, annotationPathList, isAnno, userCustomSet?.useAnnotation]);

  return (
    <div style={{ position: "relative", width: "100%", height: "100%" }}>
      <StOpenseadragon
        id={osdContainerId}
        style={{ width: "100%", height: "100%" }}
        $selectedAnnotation={annotationInfo.selectedAnnotation?.annoId}
      />

      {/* 어노테이션 정보창 컴포넌트 - 어노테이션 정보 창 핸들링 */}
      <AnnotationInfoPanelComponent {...annotationInfo} />

      {/* {userCustomSet?.useICCButton && (
        <Button
          onClick={changeColor}
          type={icc === "none" ? "primary" : "default"}
          style={{
            position: "absolute",
            height: "36.5px",
            padding: "9.25px 12px",
            top: "10px",
            right: "3px",
          }}
        >
          {icc === "none" ? "ICC" : "ICC"}
        </Button>
      )} */}
      <div style={{
        position: "absolute",
        bottom: "10px",
        right: "10px",
        display: "flex",
        gap: "8px",
        zIndex: 1000
      }}>
        {showAnnotationButton && (
          <StToggleButton
            $isActive={showAnnotationPanel}
            onClick={() => setShowAnnotationPanel(!showAnnotationPanel)}
          >
            Annotation
          </StToggleButton>
        )}
        {isUserSet && <ViewerToggleManager mode="image"/>}
      </div>

      {showAnnotationPanel && (
        <AnnotationPanel
          annotations={annotations || []}
          annotationInfo={annotationInfo}
          onClose={() => setShowAnnotationPanel(false)}
        />
      )}
    </div>
  );
};

export default OSDImageViewer;

interface StOpenseadragonProps {
  $selectedAnnotation?: number;
}

const StOpenseadragon = styled('div')<StOpenseadragonProps>(({ theme, $selectedAnnotation }) => ({
  width: '100%',
  height: '100%',

  '& .annotation': {
    cursor: 'pointer'
  },

  [`& #annotation${$selectedAnnotation}`]: {
    strokeWidth: '4 !important',
    filter: 'brightness(0.85) drop-shadow(0 0 6px currentColor) drop-shadow(0 0 12px currentColor)',

    // 화살표(g 요소)의 자식 line, path도 동일 효과
    '& line': {
      strokeWidth: '4 !important'
    },
    '& path': {
      strokeWidth: '4 !important'
    }
  },

  '& .osd-annotation-label-bg': {
    pointerEvents: 'none',
  },

  '& .osd-annotation-label': {
    fill: 'white !important',
    stroke: 'rgba(0, 0, 0, 0.8) !important',
    pointerEvents: 'none',
    userSelect: 'none',
    paintOrder: 'stroke fill',
    fontWeight: '400 !important',
    fontFamily: '"Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif !important',
  },

  '& button[style*="position: absolute"]': {
    backgroundColor: 'rgba(0, 0, 0, 0.5) !important',
    color: 'white !important',
    border: '1px solid rgba(255, 255, 255, 0.3) !important',
    borderRadius: '4px !important',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15) !important',

    '&:hover': {
      backgroundColor: 'rgba(0, 0, 0, 0.8) !important',
      borderColor: 'rgba(255, 255, 255, 0.5) !important',
    }
  },

  '& > div[style*="position: absolute"]': {
    backgroundColor: 'rgba(0, 0, 0, 0.6) !important',
    padding: '8px 12px !important',
    borderRadius: '4px !important',
    border: '1px solid rgba(255, 255, 255, 0.3) !important',

    '& input[type="range"]': {
      margin: '0 !important',

      '&::-webkit-slider-thumb': {
        WebkitAppearance: 'none',
        appearance: 'none',
        width: '14px',
        height: '14px',
        borderRadius: '50%',
        background: 'rgb(174, 203, 255)',
        cursor: 'pointer',
      },

      '&::-moz-range-thumb': {
        width: '14px',
        height: '14px',
        borderRadius: '50%',
        background: 'rgb(174, 203, 255)',
        cursor: 'pointer',
        border: 'none',
      },

      '&::-webkit-slider-runnable-track': {
        background: 'rgba(255, 255, 255, 0.9)',
        height: '6px',
        borderRadius: '3px',
      },

      '&::-moz-range-track': {
        background: 'rgba(255, 255, 255, 0.9)',
        height: '6px',
        borderRadius: '3px',
      }
    }
  }
}));

interface StToggleButtonProps {
  $isActive: boolean;
}

const StToggleButton = styled.button<StToggleButtonProps>`
  background-color: ${props => props.$isActive ? 'rgba(24, 144, 255, 0.8)' : 'rgba(0, 0, 0, 0.5)'};
  color: white;
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 4px;
  padding: 4px 10px;
  font-size: 0.85rem;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background-color: ${props => props.$isActive ? 'rgba(24, 144, 255, 0.95)' : 'rgba(0, 0, 0, 0.75)'};
    border-color: rgba(255, 255, 255, 0.5);
  }
`;
